import React from 'react';
import { Shield, ChevronRight } from 'lucide-react';

interface RiskScoreObject {
  value: string;
  trend: 'up' | 'down';
}

interface AssetRiskScoreCardProps {
  riskScore: string | RiskScoreObject;
  findings: any
}

export const AssetRiskScoreCard: React.FC<AssetRiskScoreCardProps> = ({ riskScore, findings }) => {
  const getScoreColor = (value: string) => {
    switch (value) {
      case 'A': return {
        text: 'text-emerald-500',
        bg: 'bg-emerald-500',
        glow: 'shadow-emerald-500/20',
        hover: 'group-hover:text-emerald-400',
        gradient: 'from-emerald-500/5'
      };
      case 'B': return {
        text: 'text-blue-500',
        bg: 'bg-blue-500',
        glow: 'shadow-blue-500/20',
        hover: 'group-hover:text-blue-400',
        gradient: 'from-blue-500/5'
      };
      case 'C': return {
        text: 'text-yellow-500',
        bg: 'bg-yellow-500',
        glow: 'shadow-yellow-500/20',
        hover: 'group-hover:text-yellow-400',
        gradient: 'from-yellow-500/5'
      };
      case 'D': return {
        text: 'text-orange-500',
        bg: 'bg-orange-500',
        glow: 'shadow-orange-500/20',
        hover: 'group-hover:text-orange-400',
        gradient: 'from-orange-500/5'
      };
      case 'F': return {
        text: 'text-red-500',
        bg: 'bg-red-500',
        glow: 'shadow-red-500/20',
        hover: 'group-hover:text-red-400',
        gradient: 'from-red-500/5'
      };
      default: return {
        text: 'text-gray-500',
        bg: 'bg-gray-500',
        glow: 'shadow-gray-500/20',
        hover: 'group-hover:text-gray-400',
        gradient: 'from-gray-500/5'
      };
    }
  };

  const scoreColors = getScoreColor(typeof riskScore === 'object' ? riskScore.value : riskScore);

  return (
    <div className="col-span-2 bg-gray-800/30 rounded-lg p-4 relative overflow-hidden group transition-all duration-300 hover:bg-gray-800/40">
      {/* Ambient light effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent opacity-50" />
      
      {/* Gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${scoreColors.gradient} via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${scoreColors.bg} opacity-0 group-hover:opacity-20 transition-opacity duration-500 animate-float`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">Risk Score</div>
          <Shield className={`h-4 w-4 ${scoreColors.text} transform transition-transform duration-300 group-hover:scale-110`} />
        </div>

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center space-x-4">
            {/* Score with glow effect */}
            <div className="relative">
              <div className={`text-4xl font-bold ${scoreColors.text} ${scoreColors.hover} transition-all duration-300 relative`}>
                {typeof riskScore === 'object' ? riskScore.value : riskScore}
                <div className={`absolute inset-0 ${scoreColors.glow} blur opacity-75 scale-110`} />
              </div>
              <div className={`absolute -bottom-1 left-0 w-full h-0.5 ${scoreColors.bg}/30 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`} />
            </div>

            {/* Risk indicators */}
            {/* {findings && (findings.critical > 0 || findings.high > 0) && (<div className="flex flex-col text-sm space-y-0.5">
              {findings.critical > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-gray-400">{findings.critical} Critical</span>
                </div>
              )}
              {findings.high > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  <span className="text-gray-400">{findings.high} High</span>
                </div>
              )}
            </div>)} */}
          </div>

          {/* Trend indicator */}
          <div className="flex items-center space-x-2 text-sm">
            <div className="relative overflow-hidden rounded-full p-1 bg-gray-800/50 group-hover:bg-gray-800 transition-colors duration-300">
              <ChevronRight 
                className={`h-4 w-4 transform transition-transform duration-300 ${
                  typeof riskScore === 'object' && riskScore.trend === 'down' 
                  ? 'text-green-500 rotate-90 group-hover:translate-y-0.5' 
                  : 'text-red-500 -rotate-90 group-hover:-translate-y-0.5'}`} 
              />
            </div>
            <span 
              className={`font-medium ${
                typeof riskScore === 'object' && riskScore.trend === 'down' 
                  ? 'text-green-500' 
                  : 'text-red-500'}`}
            >
              {typeof riskScore === 'object' && riskScore.trend === 'down' ? 'Improving' : 'Declining'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};